import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { filter, Subject, take, takeUntil } from 'rxjs';
import {
  AcademicYearRollover,
  RolloverNewGrade,
  RolloverResult,
  RolloverStatus,
  RolloverStudent,
  RolloverStudentStatus,
} from '../../../../models/academic-year-rollover.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { UserModel } from '../../../../models/user.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { AcademicYearService } from '../../../../services/academic-year.school.service';
import { SchoolGradeService } from '../../../../services/grade.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as groupActions from '../../../../state/actions/group.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { DlgAddGradeComponent } from '../dlg-add-grade/dlg-add-grade.component';

/** Student placement choices in step 5 besides a new-year grade */
export const PLACE_REMOVE = '__remove__';
export const PLACE_SKIP = '__skip__';
export const PLACE_UNASSIGNED = '';

export interface NewYearGrade {
  id: string;
  gradeName: string;
  added: boolean;
}

/**
 * New Academic Year wizard. Every step only saves decisions to the rollover
 * draft; Finish calls `school_academicYear_finalize`, which archives the year,
 * moves grades, teachers and students, and switches the academic year.
 */
@Component({
  selector: 'app-academic-year',
  standalone: true,
  imports: [BtnBackComponent, BtnActionComponent, InitialsPipe],
  templateUrl: './academic-year.component.html',
  styleUrl: './academic-year.component.css',
})
export class AcademicYearComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private store: Store<AppState> = inject(Store<AppState>);
  private afs = inject(AngularFirestore);
  private dialog = inject(MatDialog);
  private logger = inject(NGXLogger);
  private yearService = inject(AcademicYearService);
  private gradeService = inject(SchoolGradeService);
  private utilsService = inject(UtilsService);

  readonly steps: string[] = [
    'Academic Year',
    'Graduating Students',
    'New Grades',
    'Tag Teachers',
    'Place Students',
    'Review and Finish',
  ];
  readonly statuses = RolloverStudentStatus;
  readonly placeRemove = PLACE_REMOVE;
  readonly placeSkip = PLACE_SKIP;
  readonly placeUnassigned = PLACE_UNASSIGNED;

  groupId: string = this.activatedRoute.snapshot.paramMap.get('id')!;
  group: Group | undefined;
  user: UserModel | null = null;

  activeStep = 1;
  loading = false;
  saving = false;
  finishing = false;
  error: string | undefined;

  draft: AcademicYearRollover | undefined;
  result: RolloverResult | undefined;

  /** Active grades of the current academic year */
  currGrades: Grade[] = [];
  teachers: SchoolMember[] = [];
  students: RolloverStudent[] = [];

  selectedGradeId: string | undefined;
  selectedStudentIds = new Set<string>();

  private exit$ = new Subject<void>();

  get fromYear(): number | undefined {
    return this.group?.academicYear;
  }

  get toYear(): number | undefined {
    return this.fromYear !== undefined ? this.fromYear + 1 : undefined;
  }

  get isDone(): boolean {
    return this.draft?.status === RolloverStatus.done;
  }

  get isFinalizing(): boolean {
    return this.draft?.status === RolloverStatus.finalizing;
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => (this.user = user));

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(
        filter((group) => !!group?.academicYear),
        take(1),
        takeUntil(this.exit$),
      )
      .subscribe((group) => {
        this.group = group;
        this.loadDraft();
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  async loadDraft() {
    if (this.fromYear === undefined) return;
    this.loading = true;
    this.error = undefined;
    try {
      const [draft, grades] = await Promise.all([
        this.yearService.getDraft(this.groupId, this.fromYear),
        this.gradeService.getGradesByAcademicYear(this.groupId, this.fromYear),
      ]);
      this.draft = draft;
      this.currGrades = grades;
      this.result = draft?.result;
      this.logger.debug(
        `AcademicYearComponent :: loadDraft: ${grades.length} grades, draft ${draft?.status ?? 'none'}`,
      );
    } catch (error) {
      this.logger.error(`AcademicYearComponent :: loadDraft: ${error}`);
      this.error = 'Unable to load the New Academic Year details. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  /** Step 1: create or resume the draft, and bring its student list up to date */
  async onStart() {
    this.logger.debug(`AcademicYearComponent :: onStart: ${this.groupId}, ${this.fromYear}`);
    if (this.fromYear === undefined || !this.user?.uid) {
      this.utilsService.onShowAlert({
        code: 'ERR_MISSING_PREREQS',
        title: 'Prerequisites not met',
        message: 'Unable to fetch School information. Please login and try again.',
      });
      return;
    }
    this.loading = true;
    try {
      this.draft = await this.yearService.getOrCreateDraft(
        this.groupId,
        this.fromYear,
        this.user.uid!,
      );
      const [students, teachers] = await Promise.all([
        this.yearService.syncDraftStudents(this.groupId, this.fromYear),
        this.yearService.getActiveTeachers(this.groupId),
      ]);
      this.students = students;
      this.teachers = teachers;
      this.selectGrade(this.currGrades[0]?.id);
      this.setActiveStep(2);
    } catch (error) {
      this.logger.error(`AcademicYearComponent :: onStart: ${error}`);
      this.utilsService.onShowAlert({
        code: 'ERR_ROLLOVER_START',
        title: 'Unable to start',
        message: `Could not open the New Academic Year draft. Another leader may be editing it. ${error}`,
      });
    } finally {
      this.loading = false;
    }
  }

  setActiveStep(step: number) {
    this.logger.debug(`AcademicYearComponent :: setActiveStep: ${step}`);
    this.activeStep = step;
    this.selectedStudentIds.clear();
  }

  selectGrade(gradeId: string | undefined) {
    this.logger.debug(`AcademicYearComponent :: selectGrade: ${gradeId}`);
    this.selectedGradeId = gradeId;
    this.selectedStudentIds.clear();
  }

  /** The current-year grade a student is listed under; undefined when none of them */
  private listedGradeId(student: RolloverStudent): string | undefined {
    const gradeId = student.fromGradeId;
    return gradeId && this.currGrades.some((g) => g.id === gradeId) ? gradeId : undefined;
  }

  /**
   * Students of a current-year grade. `undefined` lists everyone not in one:
   * no grade, the legacy "unassigned" value, or a grade from another year.
   */
  studentsOf(gradeId: string | undefined): RolloverStudent[] {
    return this.students
      .filter((s) => this.listedGradeId(s) === gradeId)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  /** Active students in a grade, the ones step 5 places */
  continuingOf(gradeId: string | undefined): RolloverStudent[] {
    return this.studentsOf(gradeId).filter(
      (s) => s.status !== RolloverStudentStatus.alumni,
    );
  }

  get ungradedStudents(): RolloverStudent[] {
    return this.studentsOf(undefined);
  }

  private async saveStudentChanges(changed: RolloverStudent[]) {
    if (changed.length === 0 || this.fromYear === undefined) return;
    this.saving = true;
    try {
      await this.yearService.saveStudents(this.groupId, this.fromYear, changed);
      changed.forEach((c) => {
        const i = this.students.findIndex((s) => s.memberId === c.memberId);
        if (i >= 0) this.students[i] = c;
      });
    } catch (error) {
      this.logger.error(`AcademicYearComponent :: saveStudentChanges: ${error}`);
      this.utilsService.onShowAlert({
        code: 'ERR_ROLLOVER_SAVE',
        title: 'Unable to save',
        message: `Your change was not saved. Please try again. ${error}`,
      });
    } finally {
      this.saving = false;
    }
  }

  // ---- Step 2: graduating students -------------------------------------

  onChangeStatus(event: Event, student: RolloverStudent) {
    const status = (event.target as HTMLSelectElement).value as RolloverStudentStatus;
    this.logger.debug(
      `AcademicYearComponent :: onChangeStatus: ${student.memberId}, ${status}`,
    );
    const changed =
      status === RolloverStudentStatus.alumni
        ? { ...student, status, skip: false }
        : { ...student, status: RolloverStudentStatus.active };
    this.saveStudentChanges([changed]);
  }

  // ---- Step 3: new grades ----------------------------------------------

  isKept(gradeId: string): boolean {
    return this.draft?.keepGradeIds.includes(gradeId) ?? false;
  }

  get newGrades(): NewYearGrade[] {
    const kept = this.currGrades
      .filter((g) => this.isKept(g.id!))
      .map((g) => ({ id: g.id!, gradeName: g.gradeName, added: false }));
    const added = (this.draft?.addGrades ?? []).map((g) => ({
      id: g.id,
      gradeName: g.gradeName,
      added: true,
    }));
    return [...kept, ...added];
  }

  private get newGradeIds(): Set<string> {
    return new Set(this.newGrades.map((g) => g.id));
  }

  private async saveDraft(
    changes: Partial<Pick<AcademicYearRollover, 'keepGradeIds' | 'addGrades' | 'teachers'>>,
  ) {
    if (!this.draft || this.fromYear === undefined || !this.user?.uid) return;
    const previous = this.draft;
    this.draft = { ...this.draft, ...changes };
    this.saving = true;
    try {
      await this.yearService.saveDraft(this.groupId, this.fromYear, this.user.uid!, changes);
    } catch (error) {
      this.logger.error(`AcademicYearComponent :: saveDraft: ${error}`);
      this.draft = previous;
      this.utilsService.onShowAlert({
        code: 'ERR_ROLLOVER_SAVE',
        title: 'Unable to save',
        message: `Your change was not saved. Another leader may be editing this New Academic Year. ${error}`,
      });
    } finally {
      this.saving = false;
    }
  }

  onToggleKeep(grade: Grade) {
    this.logger.debug(`AcademicYearComponent :: onToggleKeep: ${grade.id}`);
    const keep = this.isKept(grade.id!)
      ? this.draft!.keepGradeIds.filter((id) => id !== grade.id)
      : [...this.draft!.keepGradeIds, grade.id!];
    this.saveDraft({ keepGradeIds: keep });
  }

  onAddGrade() {
    this.logger.debug(`AcademicYearComponent :: onAddGrade`);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { groupId: this.groupId, academicYear: this.toYear };
    this.dialog
      .open(DlgAddGradeComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1))
      .subscribe((grade) => {
        this.logger.debug(
          `AcademicYearComponent :: onAddGrade: result ${JSON.stringify(grade)}`,
        );
        if (!grade) return;
        const added: RolloverNewGrade = {
          id: this.afs.createId(),
          gradeName: grade.gradeName,
          gradeAvgAge: Number(grade.gradeAvgAge),
        };
        this.saveDraft({ addGrades: [...(this.draft?.addGrades ?? []), added] });
      });
  }

  onRemoveAddedGrade(gradeId: string) {
    this.logger.debug(`AcademicYearComponent :: onRemoveAddedGrade: ${gradeId}`);
    const teachers = { ...(this.draft?.teachers ?? {}) };
    delete teachers[gradeId];
    this.saveDraft({
      addGrades: (this.draft?.addGrades ?? []).filter((g) => g.id !== gradeId),
      teachers,
    });
  }

  // ---- Step 4: teachers ------------------------------------------------

  teacherOf(gradeId: string): string {
    const teacherId = this.draft?.teachers?.[gradeId];
    if (teacherId !== undefined) return teacherId ?? '';
    // Kept grades start with their current teacher
    const current = this.currGrades.find((g) => g.id === gradeId)?.teacherId;
    return current && this.teachers.some((t) => t.memberId === current) ? current : '';
  }

  /** A teacher already tagged to another grade can't be picked (one grade each) */
  isTeacherTaken(teacherId: string, gradeId: string): boolean {
    return this.newGrades.some(
      (g) => g.id !== gradeId && this.teacherOf(g.id) === teacherId,
    );
  }

  onChangeTeacher(event: Event, gradeId: string) {
    const teacherId = (event.target as HTMLSelectElement).value || null;
    this.logger.debug(
      `AcademicYearComponent :: onChangeTeacher: grade ${gradeId}, teacher ${teacherId}`,
    );
    this.saveDraft({ teachers: { ...this.currentTeachers(), [gradeId]: teacherId } });
  }

  /** The full teacher map, including kept grades' default teachers */
  private currentTeachers(): { [gradeId: string]: string | null } {
    const teachers: { [gradeId: string]: string | null } = {};
    this.newGrades.forEach((g) => (teachers[g.id] = this.teacherOf(g.id) || null));
    return teachers;
  }

  async onTeachersNext() {
    this.logger.debug(`AcademicYearComponent :: onTeachersNext`);
    await this.saveDraft({ teachers: this.currentTeachers() });
    this.selectGrade(this.currGrades[0]?.id);
    this.setActiveStep(5);
  }

  // ---- Step 5: place students ------------------------------------------

  placementOf(student: RolloverStudent): string {
    if (student.skip) return PLACE_SKIP;
    if (student.status === RolloverStudentStatus.removed) return PLACE_REMOVE;
    return student.newGradeId && this.newGradeIds.has(student.newGradeId)
      ? student.newGradeId
      : PLACE_UNASSIGNED;
  }

  private withPlacement(student: RolloverStudent, placement: string): RolloverStudent {
    if (placement === PLACE_SKIP) {
      return { ...student, status: RolloverStudentStatus.active, skip: true };
    }
    if (placement === PLACE_REMOVE) {
      return { ...student, status: RolloverStudentStatus.removed, skip: false };
    }
    return {
      ...student,
      status: RolloverStudentStatus.active,
      skip: false,
      newGradeId: placement || null,
    };
  }

  onToggleStudent(student: RolloverStudent) {
    this.logger.debug(`AcademicYearComponent :: onToggleStudent: ${student.memberId}`);
    if (this.selectedStudentIds.has(student.memberId)) {
      this.selectedStudentIds.delete(student.memberId);
    } else {
      this.selectedStudentIds.add(student.memberId);
    }
  }

  onToggleAllStudents(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.logger.debug(`AcademicYearComponent :: onToggleAllStudents: ${checked}`);
    this.selectedStudentIds.clear();
    if (checked) {
      this.continuingOf(this.selectedGradeId).forEach((s) =>
        this.selectedStudentIds.add(s.memberId),
      );
    }
  }

  /** Changing one selected student's placement applies it to every selected student */
  onChangePlacement(event: Event, student: RolloverStudent) {
    const placement = (event.target as HTMLSelectElement).value;
    const targets =
      this.selectedStudentIds.has(student.memberId) && this.selectedStudentIds.size > 1
        ? this.students.filter((s) => this.selectedStudentIds.has(s.memberId))
        : [student];
    this.logger.debug(
      `AcademicYearComponent :: onChangePlacement: ${placement} for ${targets.length} students`,
    );
    this.saveStudentChanges(targets.map((s) => this.withPlacement(s, placement)));
  }

  // ---- Step 6: review and finish ---------------------------------------

  get unassignedStudents(): RolloverStudent[] {
    return this.students.filter(
      (s) =>
        s.status === RolloverStudentStatus.active &&
        !s.skip &&
        this.placementOf(s) === PLACE_UNASSIGNED,
    );
  }

  get problems(): string[] {
    const problems: string[] = [];
    if (this.newGrades.length === 0) {
      problems.push(`No grades are set up for ${this.toYear}.`);
    }
    const unassigned = this.unassignedStudents;
    if (unassigned.length > 0) {
      problems.push(
        `${unassigned.length} student(s) have no ${this.toYear} grade: ${unassigned
          .map((s) => s.name)
          .join(', ')}. Assign a grade, remove them, or skip them.`,
      );
    }
    return problems;
  }

  get summary() {
    const counts = { promoted: 0, alumni: 0, removed: 0, skipped: 0 };
    this.students.forEach((s) => {
      if (s.skip) counts.skipped++;
      else if (s.status === RolloverStudentStatus.alumni) counts.alumni++;
      else if (s.status === RolloverStudentStatus.removed) counts.removed++;
      else counts.promoted++;
    });
    return counts;
  }

  async onFinish() {
    this.logger.debug(`AcademicYearComponent :: onFinish: ${this.groupId}, ${this.fromYear}`);
    if (this.fromYear === undefined || this.problems.length > 0) return;
    this.finishing = true;
    try {
      // Save the final teacher map (kept grades' default teachers included)
      await this.saveDraft({ teachers: this.currentTeachers() });
      this.result = await this.yearService.finalize(this.groupId, this.fromYear);
      this.draft = this.draft && { ...this.draft, status: RolloverStatus.done };
      this.store.dispatch(groupActions.clearFromStore({ id: this.groupId }));
    } catch (error: any) {
      this.logger.error(`AcademicYearComponent :: onFinish: ${error}`);
      const details: string[] = error?.details?.errors ?? [];
      this.utilsService.onShowAlert({
        code: 'ERR_ROLLOVER_FINISH',
        title: 'New Academic Year not finished',
        message: `${error?.message ?? error}${
          details.length ? ` ${details.join('; ')}` : ''
        } Nothing is lost: fix the problem and click Finish again.`,
      });
      // The server marks the draft failed, which keeps it editable
      if (this.draft) this.draft = { ...this.draft, status: RolloverStatus.failed };
    } finally {
      this.finishing = false;
    }
  }
}
